import { useState, useRef, useEffect, useCallback } from 'react'
import { chatClassSchedule } from '@/utils/chatClassSchedule'
import { useSpeechToText } from '@/hooks/useSpeechToText'
import useResizablePanel from '@/hooks/useResizablePanel'
import SparklesIcon from './icons/SparklesIcon'

const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const JS_DAY_TO_LABEL = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const EMPTY_CLASS = {
  name: '',
  startTime: '09:00',
  duration: 50,
  days: [],
  location: '',
  professor: '',
  notes: '',
}

function ClassForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial)

  function update(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  function toggleDay(day) {
    setForm((prev) => ({
      ...prev,
      days: prev.days.includes(day)
        ? prev.days.filter((d) => d !== day)
        : [...prev.days, day],
    }))
  }

  function handleSubmit(e) {
    e.preventDefault()
    if (!form.name.trim() || form.days.length === 0) return
    onSave(form)
  }

  return (
    <form className="class-form" onSubmit={handleSubmit}>
      <div className="class-form-row">
        <label className="class-form-label">
          Class Name
          <input
            className="task-input"
            type="text"
            value={form.name}
            onChange={(e) => update('name', e.target.value)}
            placeholder="e.g. Introduction to Psychology"
            autoFocus
          />
        </label>
      </div>

      <div className="class-form-row class-form-row--inline">
        <label className="class-form-label">
          Start Time
          <input
            className="task-input class-form-time"
            type="time"
            value={form.startTime}
            onChange={(e) => update('startTime', e.target.value)}
          />
        </label>
        <label className="class-form-label">
          Duration (min)
          <input
            className="task-input class-form-duration"
            type="number"
            min="15"
            max="300"
            step="5"
            value={form.duration}
            onChange={(e) => update('duration', Number(e.target.value))}
          />
        </label>
      </div>

      <fieldset className="class-form-fieldset">
        <legend className="class-form-legend">Days of Week</legend>
        <div className="class-form-days">
          {DAYS_OF_WEEK.map((day) => (
            <button
              key={day}
              type="button"
              className={`class-day-btn ${form.days.includes(day) ? 'class-day-btn--active' : ''}`}
              onClick={() => toggleDay(day)}
            >
              {day}
            </button>
          ))}
        </div>
      </fieldset>

      <div className="class-form-row">
        <label className="class-form-label">
          Location
          <input
            className="task-input"
            type="text"
            value={form.location}
            onChange={(e) => update('location', e.target.value)}
            placeholder="e.g. Science Hall 204"
          />
        </label>
      </div>

      <div className="class-form-row">
        <label className="class-form-label">
          Professor
          <input
            className="task-input"
            type="text"
            value={form.professor}
            onChange={(e) => update('professor', e.target.value)}
            placeholder="e.g. Dr. Smith"
          />
        </label>
      </div>

      <div className="class-form-row">
        <label className="class-form-label">
          Notes
          <textarea
            className="task-input class-form-notes"
            value={form.notes}
            onChange={(e) => update('notes', e.target.value)}
            placeholder="Syllabus link, website, office hours, etc."
            rows={3}
          />
        </label>
      </div>

      <div className="class-form-actions">
        <button type="submit" className="btn btn-primary" disabled={!form.name.trim() || form.days.length === 0}>
          Save
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  )
}

function formatTime(time24) {
  const [h, m] = time24.split(':').map(Number)
  const ampm = h >= 12 ? 'PM' : 'AM'
  const h12 = h % 12 || 12
  return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`
}

function WeekOverview({ classes, onClassClick }) {
  const todayLabel = JS_DAY_TO_LABEL[new Date().getDay()]

  // Build a map of day -> classes sorted by start time
  const byDay = {}
  for (const day of DAYS_OF_WEEK) {
    byDay[day] = classes
      .filter((c) => c.days.includes(day))
      .sort((a, b) => a.startTime.localeCompare(b.startTime))
  }

  const hasAnyClasses = classes.length > 0

  if (!hasAnyClasses) return null

  return (
    <div className="week-overview">
      <h3 className="week-overview-title">This Week</h3>
      <div className="week-overview-grid">
        {DAYS_OF_WEEK.map((day) => {
          const isToday = day === todayLabel
          const dayClasses = byDay[day]
          return (
            <div
              key={day}
              className={`week-day-card ${isToday ? 'week-day-card--today' : ''}`}
            >
              <div className={`week-day-header ${isToday ? 'week-day-header--today' : ''}`}>
                {day}
                {isToday && <span className="week-day-today-badge">Today</span>}
              </div>
              <div className="week-day-classes">
                {dayClasses.length === 0 ? (
                  <span className="week-day-empty">No classes</span>
                ) : (
                  dayClasses.map((cls) => (
                    <button
                      key={cls.id}
                      className="week-class-item"
                      onClick={() => onClassClick(cls.id)}
                    >
                      <span className="week-class-time">{formatTime(cls.startTime)}</span>
                      <span className="week-class-name">{cls.name}</span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function ClassCard({ cls, highlight, onEdit, onDelete }) {
  const [confirmDelete, setConfirmDelete] = useState(false)

  return (
    <div className={`class-card ${highlight ? 'class-card--highlight' : ''}`} id={`class-${cls.id}`}>
      <div className="class-card-header">
        <h3 className="class-card-name">{cls.name}</h3>
        <div className="class-card-action-buttons">
          {confirmDelete ? (
            <div className="delete-confirm">
              <span className="delete-confirm-text">Delete?</span>
              <button className="btn-confirm-yes" onClick={() => onDelete(cls.id)}>Yes</button>
              <button className="btn-confirm-no" onClick={() => setConfirmDelete(false)}>No</button>
            </div>
          ) : (
            <>
              <button className="btn-icon btn-edit-class" onClick={() => onEdit(cls)} title="Edit">&#9998;</button>
              <button className="btn-icon btn-delete-task" onClick={() => setConfirmDelete(true)} title="Delete">&#10005;</button>
            </>
          )}
        </div>
      </div>
      <div className="class-card-details">
        <div className="class-card-detail">
          <span className="class-detail-label">Schedule</span>
          <span>{cls.days.join(', ')} &middot; {formatTime(cls.startTime)} &middot; {cls.duration} min</span>
        </div>
        {cls.location && (
          <div className="class-card-detail">
            <span className="class-detail-label">Location</span>
            <span>{cls.location}</span>
          </div>
        )}
        {cls.professor && (
          <div className="class-card-detail">
            <span className="class-detail-label">Professor</span>
            <span>{cls.professor}</span>
          </div>
        )}
        {cls.notes && (
          <div className="class-card-detail class-card-detail--notes">
            <span className="class-detail-label">Notes</span>
            <span className="class-detail-notes">{cls.notes}</span>
          </div>
        )}
      </div>
    </div>
  )
}

function ClassSchedule({ classes, onAddClass, onUpdateClass, onDeleteClass }) {
  const [showForm, setShowForm] = useState(false)
  const [editingClass, setEditingClass] = useState(null)
  const [highlightId, setHighlightId] = useState(null)

  // Chat state
  const [chatOpen, setChatOpen] = useState(false)
  const [chatMessages, setChatMessages] = useState([])
  const [chatInput, setChatInput] = useState('')
  const [chatLoading, setChatLoading] = useState(false)
  const chatMessagesEndRef = useRef(null)
  const { isListening, startListening, stopListening, isSupported: micSupported, interimText } = useSpeechToText(
    useCallback((text) => setChatInput((prev) => prev ? prev + ' ' + text : text), [])
  )
  const { chatWidth, containerRef, onPointerDown } = useResizablePanel()

  // Confirmation state for AI-proposed changes
  const [pendingClasses, setPendingClasses] = useState(null)
  const [pendingAction, setPendingAction] = useState(null)
  const [pendingDeleteIds, setPendingDeleteIds] = useState([])

  useEffect(() => {
    if (chatMessagesEndRef.current) {
      chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [chatMessages])

  async function handleChatSend() {
    const message = chatInput.trim()
    if (!message || chatLoading) return

    const userMsg = { role: 'user', text: message }
    setChatMessages((prev) => [...prev, userMsg])
    setChatInput('')
    setChatLoading(true)

    try {
      const result = await chatClassSchedule({
        userMessage: message,
        classes,
        chatHistory: chatMessages,
      })

      setChatMessages((prev) => [...prev, { role: 'assistant', text: result.message }])

      if (result.type === 'classes' && Array.isArray(result.classes)) {
        setPendingClasses(result.classes)
        setPendingAction(result.action || 'add')
        setPendingDeleteIds(result.deleteIds || [])
      }
    } catch {
      setChatMessages((prev) => [...prev, { role: 'assistant', text: 'Sorry, something went wrong. Please try again.' }])
    } finally {
      setChatLoading(false)
    }
  }

  function handleConfirmClasses() {
    if (!pendingClasses) return

    if (pendingAction === 'delete') {
      for (const id of pendingDeleteIds) {
        onDeleteClass(id)
      }
    } else if (pendingAction === 'update') {
      // Replace all: delete existing, add updated list
      for (const cls of classes) {
        onDeleteClass(cls.id)
      }
      pendingClasses.forEach((cls, i) => {
        onAddClass({ ...cls, id: Date.now().toString() + i })
      })
    } else {
      // "add" — append new classes
      pendingClasses.forEach((cls, i) => {
        onAddClass({ ...cls, id: Date.now().toString() + i })
      })
    }

    setChatMessages((prev) => [...prev, { role: 'assistant', text: 'Changes applied!' }])
    setPendingClasses(null)
    setPendingAction(null)
    setPendingDeleteIds([])
  }

  function handleRejectClasses() {
    setChatMessages((prev) => [...prev, { role: 'assistant', text: 'Changes discarded. Feel free to ask for something else.' }])
    setPendingClasses(null)
    setPendingAction(null)
    setPendingDeleteIds([])
  }

  function handleCloseChat() {
    setChatOpen(false)
    setChatMessages([])
    setChatInput('')
    setPendingClasses(null)
    setPendingAction(null)
    setPendingDeleteIds([])
  }

  function handleSaveNew(form) {
    onAddClass({ ...form, id: Date.now().toString() })
    setShowForm(false)
  }

  function handleSaveEdit(form) {
    onUpdateClass(editingClass.id, form)
    setEditingClass(null)
  }

  function handleEdit(cls) {
    setEditingClass(cls)
    setShowForm(false)
  }

  function scrollToClass(classId) {
    const el = document.getElementById(`class-${classId}`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      setHighlightId(classId)
      setTimeout(() => setHighlightId(null), 1500)
    }
  }

  return (
    <div ref={containerRef} className={chatOpen ? 'class-schedule--chat-open' : ''}>
      <div className="class-schedule-content">
      <h2 className="page-title">Class Schedule</h2>
      <p className="page-description">Manage your classes for the term. Include any events that are fixed every day at a specific time like "theater practice", etc.</p>

      <WeekOverview classes={classes} onClassClick={scrollToClass} />

      {!showForm && !editingClass && (
        <div className="class-action-buttons">
          <button className="btn btn-primary class-add-btn" onClick={() => setShowForm(true)}>
            + Add Class
          </button>
          {!chatOpen && (
            <button
              className="btn-icon btn-chat-ai"
              onClick={() => setChatOpen(true)}
              title="Chat with AI"
              aria-label="Chat with AI"
            >
              <SparklesIcon />
            </button>
          )}
        </div>
      )}

      {showForm && (
        <div className="class-form-card">
          <h3 className="class-form-title">New Class</h3>
          <ClassForm
            initial={EMPTY_CLASS}
            onSave={handleSaveNew}
            onCancel={() => setShowForm(false)}
          />
        </div>
      )}

      {editingClass && (
        <div className="class-form-card">
          <h3 className="class-form-title">Edit Class</h3>
          <ClassForm
            key={editingClass.id}
            initial={editingClass}
            onSave={handleSaveEdit}
            onCancel={() => setEditingClass(null)}
          />
        </div>
      )}

      {classes.length === 0 && !showForm ? (
        <div className="task-list-empty">
          <p className="placeholder-text">No classes added yet. Add your first class to get started!</p>
        </div>
      ) : (
        <div className="class-list">
          {classes.map((cls) => (
            <ClassCard
              key={cls.id}
              cls={cls}
              highlight={cls.id === highlightId}
              onEdit={handleEdit}
              onDelete={onDeleteClass}
            />
          ))}
        </div>
      )}

      </div>{/* end class-schedule-content */}
      {chatOpen && (
        <>
        <div className="chat-resize-handle" onMouseDown={onPointerDown} onTouchStart={onPointerDown} />
        <div className="schedule-chat-fixed" style={{ width: chatWidth }}>
          <div className="schedule-chat-fixed-header">
            <span>Chat with AI about your class schedule</span>
            <button className="btn-icon schedule-chat-close" onClick={handleCloseChat}>✕</button>
          </div>
          <div className="schedule-chat-messages">
            {chatMessages.length === 0 && (
              <p className="schedule-chat-placeholder">Paste your schedule or describe changes...</p>
            )}
            {chatMessages.map((msg, i) => (
              <div key={i} className={`schedule-chat-message schedule-chat-message--${msg.role}`}>
                <p className="schedule-chat-text">{msg.text}</p>
              </div>
            ))}
            {pendingClasses && (
              <div className="chat-class-preview">
                <p className="chat-class-preview-title">
                  {pendingAction === 'delete' ? 'Classes to remove:' : 'Proposed classes:'}
                </p>
                {pendingAction === 'delete' ? (
                  pendingDeleteIds.map((id) => {
                    const cls = classes.find((c) => c.id === id)
                    return cls ? (
                      <div key={id} className="chat-class-preview-item">
                        <strong>{cls.name}</strong> — {cls.days.join(', ')} {formatTime(cls.startTime)}
                      </div>
                    ) : null
                  })
                ) : (
                  pendingClasses.map((cls, i) => (
                    <div key={i} className="chat-class-preview-item">
                      <strong>{cls.name}</strong>
                      {cls.days.length > 0 && <span> — {cls.days.join(', ')}</span>}
                      {cls.startTime && <span> {formatTime(cls.startTime)}</span>}
                      {cls.duration && <span> ({cls.duration} min)</span>}
                      {cls.location && <span> at {cls.location}</span>}
                      {cls.professor && <span> &middot; {cls.professor}</span>}
                    </div>
                  ))
                )}
                <div className="chat-class-preview-actions">
                  <button className="btn btn-primary" onClick={handleConfirmClasses}>Apply</button>
                  <button className="btn btn-secondary" onClick={handleRejectClasses}>Cancel</button>
                </div>
              </div>
            )}
            {chatLoading && (
              <div className="schedule-chat-message schedule-chat-message--assistant">
                <p className="schedule-chat-text schedule-chat-typing">Thinking...</p>
              </div>
            )}
            <div ref={chatMessagesEndRef} />
          </div>
          <div className="schedule-chat-input-row">
            <textarea
              className="schedule-chat-input schedule-chat-textarea"
              placeholder="Paste your schedule or describe changes..."
              value={interimText ? (chatInput ? chatInput + ' ' + interimText : interimText) : chatInput}
              onChange={(e) => { if (!isListening) setChatInput(e.target.value) }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleChatSend()
                }
              }}
              onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 350)}
              disabled={chatLoading}
              rows={2}
            />
            {/* Desktop: show both buttons; Mobile: single combined mic/send button */}
            {micSupported && (
              <button
                className={`btn schedule-chat-mic schedule-chat-mic--desktop${isListening ? ' schedule-chat-mic--active' : ''}`}
                onClick={isListening ? stopListening : startListening}
                disabled={chatLoading}
                aria-label={isListening ? 'Stop dictation' : 'Start dictation'}
                type="button"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="1" width="6" height="12" rx="3" />
                  <path d="M5 10a7 7 0 0 0 14 0" />
                  <line x1="12" y1="17" x2="12" y2="21" />
                  <line x1="8" y1="21" x2="16" y2="21" />
                </svg>
              </button>
            )}
            <button
              className="btn btn-primary schedule-chat-send schedule-chat-send--desktop"
              onClick={handleChatSend}
              disabled={chatLoading || !chatInput.trim()}
              aria-label="Send"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="19" x2="12" y2="5" />
                <polyline points="5 12 12 5 19 12" />
              </svg>
            </button>
            {/* Mobile combined button: mic when empty, send when has text, stop when listening */}
            {isListening ? (
              <button
                className="btn schedule-chat-mic schedule-chat-mic--active schedule-chat-action--mobile"
                onClick={stopListening}
                type="button"
                aria-label="Stop dictation"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="6" y="6" width="12" height="12" rx="2" />
                </svg>
              </button>
            ) : chatInput.trim() ? (
              <button
                className="btn btn-primary schedule-chat-send schedule-chat-action--mobile"
                onClick={handleChatSend}
                disabled={chatLoading}
                aria-label="Send"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="19" x2="12" y2="5" />
                  <polyline points="5 12 12 5 19 12" />
                </svg>
              </button>
            ) : micSupported ? (
              <button
                className="btn schedule-chat-mic schedule-chat-action--mobile"
                onClick={startListening}
                disabled={chatLoading}
                type="button"
                aria-label="Start dictation"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="1" width="6" height="12" rx="3" />
                  <path d="M5 10a7 7 0 0 0 14 0" />
                  <line x1="12" y1="17" x2="12" y2="21" />
                  <line x1="8" y1="21" x2="16" y2="21" />
                </svg>
              </button>
            ) : (
              <button
                className="btn btn-primary schedule-chat-send schedule-chat-action--mobile"
                onClick={handleChatSend}
                disabled={chatLoading || !chatInput.trim()}
                aria-label="Send"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="19" x2="12" y2="5" />
                  <polyline points="5 12 12 5 19 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
        </>
      )}
    </div>
  )
}

export default ClassSchedule
